import { Play, Settings, Trash2, ToggleLeft, ToggleRight, Clock, Calendar } from 'lucide-react'
import type { SyncSchedule, SyncStatus } from '../types'
import { CONTENT_TYPE_ICONS, FREQUENCY_LABELS, SYNC_STATUS_CONFIG } from '../types'

type Props = {
  schedules: SyncSchedule[]
  loading: boolean
  onEdit: (schedule: SyncSchedule) => void
  onDelete: (schedule: SyncSchedule) => void
  onToggle: (schedule: SyncSchedule) => void
  onTrigger: (schedule: SyncSchedule) => void
  selectedId?: string | null
  onSelect?: (schedule: SyncSchedule) => void
}

function formatNextSync(dateStr: string | null): string {
  if (!dateStr) return 'Non planifié'
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = date.getTime() - now.getTime()
  const diffH = Math.round(diffMs / (1000 * 60 * 60))
  
  if (diffH < 0) return 'En retard'
  if (diffH < 1) return 'Dans moins d\'1h'
  if (diffH < 24) return `Dans ${diffH}h`
  const diffD = Math.round(diffH / 24)
  return `Dans ${diffD}j`
}

function formatLastSync(dateStr: string | null): string {
  if (!dateStr) return 'Jamais'
  const date = new Date(dateStr)
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function StatusBadge({ status }: { status: SyncStatus | null }) {
  if (!status) return null
  const config = SYNC_STATUS_CONFIG[status]
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${config.color}`}>
      <span>{config.icon}</span>
      <span>{config.label}</span>
    </span>
  )
}

export function SyncScheduleList({
  schedules,
  loading,
  onEdit,
  onDelete,
  onToggle,
  onTrigger,
  selectedId,
  onSelect,
}: Props) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  if (schedules.length === 0) {
    return (
      <div className="text-center py-12 text-zinc-500 dark:text-zinc-400">
        <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p className="text-lg font-medium">Aucune synchronisation configurée</p>
        <p className="text-sm mt-1">Créez votre première configuration pour synchroniser automatiquement vos contenus WordPress</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {schedules.map(schedule => {
        const isSelected = selectedId === schedule.public_id
        const icon = CONTENT_TYPE_ICONS[schedule.content_type] || '📄'
        
        return (
          <div
            key={schedule.public_id}
            onClick={() => onSelect?.(schedule)}
            className={`
              relative p-4 rounded-lg border transition-all cursor-pointer
              ${isSelected 
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                : 'border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600 bg-white dark:bg-zinc-800'}
              ${!schedule.enabled ? 'opacity-60' : ''}
            `}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-2xl flex-shrink-0">{icon}</span>
                <div className="min-w-0">
                  <h3 className="font-medium text-zinc-900 dark:text-zinc-100 truncate">
                    {schedule.name}
                  </h3>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400">
                    {schedule.content_type_label}
                    {schedule.target_collection_name && (
                      <span> → {schedule.target_collection_name}</span>
                    )}
                  </p>
                </div>
              </div>
              
              {/* Toggle */}
              <button
                onClick={(e) => { e.stopPropagation(); onToggle(schedule) }}
                className={`flex-shrink-0 p-1 rounded transition-colors ${
                  schedule.enabled 
                    ? 'text-emerald-600 hover:bg-emerald-100 dark:hover:bg-emerald-900/30' 
                    : 'text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-700'
                }`}
                title={schedule.enabled ? 'Désactiver' : 'Activer'}
              >
                {schedule.enabled ? <ToggleRight className="w-6 h-6" /> : <ToggleLeft className="w-6 h-6" />}
              </button>
            </div>
            
            {/* Info row */}
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-zinc-600 dark:text-zinc-400">
              <span className="inline-flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {FREQUENCY_LABELS[schedule.frequency]}
              </span>
              
              {schedule.enabled && schedule.next_sync_at && (
                <span className="text-blue-600 dark:text-blue-400">
                  ⏰ {formatNextSync(schedule.next_sync_at)}
                </span>
              )}
              
              {schedule.last_sync_at && (
                <span>
                  Dernière: {formatLastSync(schedule.last_sync_at)}
                </span>
              )}
              
              <StatusBadge status={schedule.last_sync_status} />
            </div>
            
            {/* Actions */}
            <div className="mt-3 pt-3 border-t border-zinc-100 dark:border-zinc-700 flex items-center gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); onTrigger(schedule) }}
                disabled={!schedule.enabled}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md
                  bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed
                  transition-colors"
              >
                <Play className="w-4 h-4" />
                Lancer
              </button>
              
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(schedule) }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md
                  text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700
                  transition-colors"
              >
                <Settings className="w-4 h-4" />
                Modifier
              </button>
              
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(schedule) }}
                className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md
                  text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20
                  transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default SyncScheduleList
